import { useCallback, useEffect, useRef, useState } from 'react';
import type { UserItem } from '@/types/userItem';
import { haptics } from '@/lib/haptics';

export function useInventorySelection(filteredItems: UserItem[]) {
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  const lastSelectedIdRef = useRef<string | null>(null);

  const handleEnterSelectionMode = useCallback(() => {
    setIsSelectionMode(true);
    setSelectedItemIds(new Set());
    lastSelectedIdRef.current = null;
  }, []);

  const handleExitSelectionMode = useCallback(() => {
    setIsSelectionMode(false);
    setSelectedItemIds(new Set());
    lastSelectedIdRef.current = null;
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedItemIds(new Set());
    setIsSelectionMode(false);
  }, []);

  const handleToggleSelection = useCallback((itemId: string, opts?: { shift?: boolean }) => {
    setSelectedItemIds(prev => {
      const next = new Set(prev);
      if (opts?.shift && lastSelectedIdRef.current && lastSelectedIdRef.current !== itemId) {
        const ids = filteredItems.map(i => i.id);
        const a = ids.indexOf(lastSelectedIdRef.current);
        const b = ids.indexOf(itemId);
        if (a >= 0 && b >= 0) {
          const [lo, hi] = a < b ? [a, b] : [b, a];
          for (let i = lo; i <= hi; i++) next.add(ids[i]);
          lastSelectedIdRef.current = itemId;
          haptics.tap();
          return next;
        }
      }
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      lastSelectedIdRef.current = itemId;
      haptics.tap();
      return next;
    });
  }, [filteredItems]);

  const handleSelectAll = useCallback(() => {
    setSelectedItemIds(new Set(filteredItems.map(item => item.id)));
  }, [filteredItems]);

  const handleDeselectAll = useCallback(() => {
    setSelectedItemIds(new Set());
  }, []);

  // Keyboard shortcuts: Esc exits, Cmd/Ctrl+A selects all
  useEffect(() => {
    if (!isSelectionMode) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleExitSelectionMode();
      } else if ((e.metaKey || e.ctrlKey) && (e.key === 'a' || e.key === 'A')) {
        const target = e.target as HTMLElement | null;
        const tag = target?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;
        e.preventDefault();
        setSelectedItemIds(new Set(filteredItems.map(i => i.id)));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isSelectionMode, filteredItems, handleExitSelectionMode]);

  return {
    isSelectionMode,
    setIsSelectionMode,
    selectedItemIds,
    setSelectedItemIds,
    handleEnterSelectionMode,
    handleExitSelectionMode,
    clearSelection,
    handleToggleSelection,
    handleSelectAll,
    handleDeselectAll,
  };
}
